import { Icon } from "@iconify/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import type { NotificationDto } from "@/api/notifications";
import { Loader } from "@/components/motion/loader";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTopbarIsland } from "@/features/workbench/topbar-island-context";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { NotificationListItem } from "./notification-list-item";
import { notificationActionRoute } from "./notification-presentation";
import { useNotifications } from "./notification-provider";
import { notificationQueryKeys } from "./notification-query";
import { useNotificationList } from "./use-notification-list";
import { WebPushOnboarding } from "./web-push-onboarding";

const PAGE_SIZE = 20;

export function NotificationCenter() {
  const { intlLocale, t } = useI18n();
  const queryClient = useQueryClient();
  const { show: showTopbarIsland } = useTopbarIsland();
  const { markAllRead, markRead, realtimeNotificationSequence, refresh, unreadCount, unreadCountPending } = useNotifications();
  const [open, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [feedbackSequence, setFeedbackSequence] = useState(0);
  const unreadCountInitializedRef = useRef(false);
  const previousUnreadCountRef = useRef(0);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (realtimeNotificationSequence === 0) {
      return;
    }

    setFeedbackSequence((sequence) => sequence + 1);
  }, [realtimeNotificationSequence]);

  useEffect(() => {
    if (unreadCountPending) {
      return;
    }

    if (!unreadCountInitializedRef.current) {
      unreadCountInitializedRef.current = true;
      previousUnreadCountRef.current = unreadCount;
      return;
    }

    const previousUnreadCount = previousUnreadCountRef.current;
    previousUnreadCountRef.current = unreadCount;
    if (unreadCount > previousUnreadCount) {
      setFeedbackSequence((sequence) => sequence + 1);
    }
  }, [unreadCount, unreadCountPending]);

  useEffect(() => {
    if (feedbackSequence === 0) {
      return;
    }

    if (openRef.current) {
      return;
    }

    if (document.visibilityState === "visible") {
      showTopbarIsland("notification", { unreadCount });
    }
  }, [feedbackSequence, showTopbarIsland, unreadCount]);

  const { newestCursor, notifications, query: listQuery } = useNotificationList({ enabled: open, pageSize: PAGE_SIZE, unreadOnly });
  const numberFormatter = new Intl.NumberFormat(intlLocale);
  const badge = unreadCount > 99 ? "99+" : numberFormatter.format(unreadCount);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      void refresh();
    } else {
      void queryClient.cancelQueries({ queryKey: notificationQueryKeys.lists() });
    }
  };

  const handleSelect = (notification: NotificationDto) => {
    if (notification.readTime === null) {
      // 已读状态先在本地乐观更新，再在后台持久化；消息跳转不等待网络请求。
      void markRead(notification.cursor).catch(() => {
        // 写操作错误已由统一请求拦截器提示，Provider 会刷新服务端状态。
      });
    }

    const route = notificationActionRoute(notification);
    if (route) {
      setOpen(false);
    } else if (!notification.isTargetAccessible) {
      toast.info(t("notifications.relatedUnavailable"));
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await markAllRead(newestCursor || undefined);
      toast.success(t("notifications.markAllReadSuccess"));
    } catch {
      // 写操作错误已由统一请求拦截器提示。
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <>
      <WebPushOnboarding suppressed={open} />
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            aria-label={unreadCount > 0 ? t("notifications.unreadCount", { count: numberFormatter.format(unreadCount) }) : t("notifications.title")}
            className="relative size-10 rounded-xl"
            size="icon"
            variant="ghost"
          >
            <Icon icon="tabler:bell" />
            {unreadCount > 0 ? (
              <span
                className={cn(
                  "absolute top-0 grid min-w-3.5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-3.5 text-destructive-foreground",
                  unreadCount > 19 ? "-right-1.5" : "right-0",
                )}
              >
                {badge}
              </span>
            ) : null}
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-[calc(100vw-1.5rem)] max-w-96 gap-0 overflow-hidden rounded-xl p-0 shadow-lg" sideOffset={8}>
          <div className="px-5 pt-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold tracking-tight text-foreground">{t("notifications.title")}</h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button asChild aria-label={t("notifications.openCenter")} size="icon-sm" variant="ghost">
                      <Link to="/notifications" onClick={() => setOpen(false)}>
                        <Icon icon="tabler:arrow-up-right" className="text-muted-foreground" />
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("notifications.openCenter")}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <Tabs className="mt-2 mb-1 gap-0" value={unreadOnly ? "unread" : "all"} onValueChange={(value) => setUnreadOnly(value === "unread")}>
              <TabsList className="h-9 p-1">
                <TabsTrigger className="h-7 flex-none px-3.5 text-xs" value="all">
                  {t("notifications.all")}
                </TabsTrigger>
                <TabsTrigger className="h-7 flex-none px-3.5 text-xs" value="unread">
                  {t("notifications.unread")}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <ScrollArea className="h-80 [&_[data-radix-scroll-area-viewport]>div]:block!">
            {listQuery.isPending ? (
              <div className="grid h-64 place-items-center text-muted-foreground">
                <span className="flex items-center gap-2 text-sm">
                  <Loader className="text-current" label={t("notifications.loading")} size={16} /> {t("notifications.loading")}
                </span>
              </div>
            ) : listQuery.isError ? (
              <div className="grid h-64 place-items-center px-6 text-center">
                <div>
                  <Icon className="mx-auto size-8 text-muted-foreground" icon="tabler:cloud-off" />
                  <p className="mt-2 text-sm font-medium text-foreground">{t("notifications.loadFailed")}</p>
                  <Button className="mt-3" size="sm" variant="outline" onClick={() => listQuery.refetch()}>
                    {t("notifications.retry")}
                  </Button>
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="grid h-64 place-items-center px-6 text-center">
                <div>
                  <Icon className="mx-auto size-8 text-muted-foreground" icon={unreadOnly ? "tabler:checks" : "tabler:bell-off"} />
                  <p className="mt-2 text-sm font-medium text-foreground">{t(unreadOnly ? "notifications.emptyUnread" : "notifications.emptyAll")}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{t(unreadOnly ? "notifications.emptyUnreadDetail" : "notifications.emptyAllDetail")}</p>
                </div>
              </div>
            ) : (
              <div className="w-full min-w-0 overflow-hidden px-3 py-2.5">
                {notifications.map((notification) => (
                  <NotificationListItem key={notification.cursor} notification={notification} onSelect={handleSelect} />
                ))}
              </div>
            )}
          </ScrollArea>

          <div className="flex min-h-14 items-center justify-between gap-3 px-5 py-2.5">
            <Button className="-ml-2 text-primary" disabled={unreadCount === 0 || markingAll} size="sm" variant="ghost" onClick={handleMarkAllRead}>
              {markingAll ? <Loader className="text-current" label={t("notifications.markingAllRead")} size={16} /> : <Icon icon="tabler:checks" />}
              {t("notifications.markAllRead")}
            </Button>
            {listQuery.hasNextPage ? (
              <Button disabled={listQuery.isFetchingNextPage} size="sm" onClick={() => listQuery.fetchNextPage()}>
                {listQuery.isFetchingNextPage ? <Loader className="text-current" label={t("notifications.loadingMore")} size={16} /> : null}
                {t("notifications.loadMore")}
              </Button>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
